import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Video, Loader2 } from 'lucide-react';

const TYPES = [
  { key: 'first_login', title: 'Welcome Video (First Login)', desc: "Plays only on a user's first-ever visit to the portal." },
  { key: 'returning',   title: 'Returning Video',            desc: 'Plays on every visit after the first.' },
];

const BLOCK_PREVIEWS = [
  { key: 'coaching',    title: 'Coaching Program' },
  { key: 'workshops',   title: 'Workshops' },
  { key: 'marketplace', title: 'Marketplace' },
  { key: 'booking',     title: 'Session Booking' },
  { key: 'events',      title: 'Events (Calendar)' },
];

const VideoSlot = ({ meta, type, title, desc, onUpload, onDelete, uploading }) => {
  const inputRef = useRef(null);
  return (
    <div className="border rounded-lg p-4 space-y-3">
      <div>
        <h3 className="font-semibold flex items-center gap-2"><Video className="w-4 h-4 text-cyan-600" /> {title}</h3>
        <p className="text-xs text-gray-500">{desc}</p>
      </div>

      {meta?.url ? (
        <div className="space-y-2">
          <video
            src={`${process.env.REACT_APP_BACKEND_URL}${meta.url}`}
            controls
            className="w-full max-w-md rounded-md border"
            style={{ maxHeight: 220 }}
            data-testid={`portal-video-preview-${type}`}
          />
          <p className="text-xs text-green-600 truncate">✓ {meta.filename || 'Uploaded'}</p>
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic">No video uploaded yet.</p>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        className="hidden"
        data-testid={`portal-video-input-${type}`}
        onChange={(e) => { if (e.target.files[0]) onUpload(type, e.target.files[0]); e.target.value = ''; }}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          className="btn-primary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`portal-video-upload-${type}`}
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
          {meta?.url ? 'Replace' : 'Upload'}
        </Button>
        {meta?.url && (
          <Button size="sm" variant="destructive" disabled={uploading} onClick={() => onDelete(type)} data-testid={`portal-video-delete-${type}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export const AdminPortalVideoTab = () => {
  const [videos, setVideos] = useState({ first_login: null, returning: null });
  const [previews, setPreviews] = useState({});
  const [uploadingType, setUploadingType] = useState(null);

  const applyRes = (data) => {
    setVideos({ first_login: data.first_login, returning: data.returning });
    setPreviews(data.block_previews || {});
  };

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/portal/admin/videos`);
      applyRes(res.data);
    } catch (e) {
      toast.error('Failed to load portal videos');
    }
  };

  useEffect(() => { fetchVideos(); }, []);

  const handleUpload = async (type, file) => {
    setUploadingType(type);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('video_type', type);
    try {
      const res = await axios.post(`${API}/portal/admin/videos`, fd);
      applyRes(res.data);
      toast.success('Video uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingType(null);
    }
  };

  const handleDelete = async (type) => {
    if (!window.confirm('Remove this video?')) return;
    try {
      const res = await axios.delete(`${API}/portal/admin/videos/${type}`);
      applyRes(res.data);
      toast.success('Video removed');
    } catch (e) {
      toast.error('Failed to remove video');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Portal Welcome Videos</CardTitle>
          <p className="text-sm text-gray-500">
            These play in the center of the members' portal landing page. Max 300MB each (MP4 recommended).
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {TYPES.map((t) => (
            <VideoSlot
              key={t.key}
              type={t.key}
              title={t.title}
              desc={t.desc}
              meta={videos[t.key]}
              onUpload={handleUpload}
              onDelete={handleDelete}
              uploading={uploadingType === t.key}
            />
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Block Hover Previews</CardTitle>
          <p className="text-sm text-gray-500">
            Short, muted, sped-up clips that play when a member hovers each portal block. Optional — blocks show an icon if no clip is set.
          </p>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {BLOCK_PREVIEWS.map((b) => (
            <VideoSlot
              key={b.key}
              type={b.key}
              title={b.title}
              desc="Hover preview clip"
              meta={previews[b.key]}
              onUpload={handleUpload}
              onDelete={handleDelete}
              uploading={uploadingType === b.key}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPortalVideoTab;
